<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import IconBox from '@uiKit/components/IconBox.vue'
import {
  formatActivityDate,
  getTransactionVisual,
  TRANSACTION_STATUS_COLOR,
  TRANSACTION_STATUS_LABEL,
} from '../utils/transactionVisualUtils'
import type { Transaction } from '../types/transactionTypes'

interface Props {
  transaction: Transaction
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'view-details', tx: Transaction): void
}>()

const visual = computed(() => getTransactionVisual(props.transaction.type))

// Rental payouts lead with the property and the period they cover; every other
// leg leads with its category.
const subtitle = computed(() => {
  const { category, createdAt, date, propertyName, rentalPeriod } = props.transaction
  const label = formatActivityDate(createdAt) || date
  const lead = props.transaction.type === 'rental_income' && propertyName ? propertyName : category
  return [lead, rentalPeriod, label].filter(Boolean).join(' · ')
})
</script>

<template>
  <Box
    is="button"
    align="center"
    gap="4"
    class="wtr"
    type="button"
    :aria-label="`View details for ${transaction.title}`"
    @click="emit('view-details', transaction)"
  >
    <Box
      v-if="visual.iconComponent"
      flex
      align="center"
      justify="center"
      class="wtr__badge"
      :style="{ background: visual.bg }"
    >
      <component :is="visual.iconComponent" />
    </Box>
    <IconBox
      v-else
      :icon="visual.icon"
      :icon-color="visual.color"
      :bg="visual.bg"
      size="xl"
      class="wtr__icon"
    />

    <Box direction="col" gap="1" grow class="wtr__mid">
      <Text type="heading" weight="medium" align="left" is="span" truncate class="wtr__title">
        {{ transaction.title }}
      </Text>
      <Text type="body" color="muted" align="left" is="span" truncate class="wtr__sub">
        {{ subtitle }}
      </Text>
    </Box>

    <Box class="wtr__status">
      <Text
        type="body"
        weight="medium"
        align="left"
        is="span"
        :color="TRANSACTION_STATUS_COLOR[transaction.status]"
      >
        {{ TRANSACTION_STATUS_LABEL[transaction.status] }}
      </Text>
    </Box>

    <Box justify="end" class="wtr__amount">
      <Text type="heading" weight="semibold" align="right" is="span" class="wtr__amount-value">
        <CurrencyText :text="transaction.amount" />
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.wtr {
  width: 100%;
  background: transparent;
  border: none;
  border-top: 1px solid var(--p-content-border-color);
  padding: 1rem 0;
  min-height: 79px;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  transition: background 0.15s;
}

.wtr:hover {
  background: var(--p-surface-50);
}

.wtr:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: -2px;
  border-radius: 8px;
}

/* Design draws the leg icon as a circle; IconBox ships a rounded square. */
.wtr :deep(.icon-box) {
  border-radius: 50%;
}

/* Same 46px circle, for legs whose mark is a kit icon component. */
.wtr__badge {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  flex-shrink: 0;
}

.wtr__mid {
  min-width: 0;
}

.wtr__status {
  width: 140px;
  flex-shrink: 0;
}

.wtr__amount {
  width: 180px;
  flex-shrink: 0;
}

.wtr__amount-value {
  font-variant-numeric: tabular-nums;
}

/* Below tablet the four columns don't fit — the row folds into icon + a
   two-line content block, with status and amount sharing the second line. */
@media (max-width: 992px) {
  .wtr {
    display: grid;
    grid-template-columns: 46px minmax(0, 1fr);
    grid-template-areas:
      'icon mid'
      'icon meta';
    column-gap: 1rem;
    row-gap: 0.375rem;
    align-items: center;
    min-height: unset;
  }

  .wtr :deep(.icon-box),
  .wtr__badge {
    grid-area: icon;
    align-self: start;
  }

  .wtr__mid {
    grid-area: mid;
  }

  .wtr__status {
    grid-area: meta;
    width: auto;
    justify-self: start;
  }

  .wtr__amount {
    grid-area: meta;
    width: auto;
    justify-self: end;
  }
}
</style>
